import { useState } from 'react';
import { motion } from 'framer-motion';
import { Database, Search, Filter, Beaker, Droplets, Zap, Shield, Wrench, Bug } from 'lucide-react';
import PageHeader from '@/components/PageHeader';

const categories = [
  { id: 'all', label: 'All Equipment', icon: Database },
  { id: 'pre', label: 'Pre-Treatment', icon: Filter },
  { id: 'primary', label: 'Primary', icon: Droplets },
  { id: 'secondary', label: 'Secondary', icon: Bug },
  { id: 'tertiary', label: 'Tertiary', icon: Beaker },
  { id: 'polishing', label: 'Polishing', icon: Shield },
  { id: 'sludge', label: 'Sludge', icon: Wrench },
  { id: 'disinfection', label: 'Disinfection', icon: Zap },
];

const equipment = [
  { name: 'Equalization Tank', category: 'pre', type: 'Flow balancing', bod: 0, cod: 0, tss: 10, footprint: 'Large', energy: 'Low', capex: 'Medium', opex: 'Low', desc: 'Balances flow and load variations to protect downstream processes.' },
  { name: 'Fine Screen (1-6mm)', category: 'pre', type: 'Screening', bod: 0, cod: 0, tss: 20, footprint: 'Small', energy: 'Low', capex: 'Low', opex: 'Low', desc: 'Removes coarse solids to protect downstream equipment.' },
  { name: 'Aerated Grit Chamber', category: 'pre', type: 'Grit removal', bod: 0, cod: 0, tss: 10, footprint: 'Medium', energy: 'Medium', capex: 'Medium', opex: 'Medium', desc: 'Removes sand and grit particles by aerated settling.' },
  { name: 'DAF — Rectangular', category: 'primary', type: 'Flotation', bod: 30, cod: 25, tss: 90, footprint: 'Medium', energy: 'Medium', capex: 'High', opex: 'Medium', desc: 'Dissolved air flotation for oil, grease and solids removal.' },
  { name: 'DAF — Circular', category: 'primary', type: 'Flotation', bod: 25, cod: 20, tss: 85, footprint: 'Medium', energy: 'Medium', capex: 'High', opex: 'Medium', desc: 'Circular DAF unit for high solids loading applications.' },
  { name: 'Coag/Flocc Basin', category: 'primary', type: 'Chemical', bod: 15, cod: 15, tss: 80, footprint: 'Small', energy: 'Low', capex: 'Low', opex: 'Low', desc: 'Rapid mix and flocculation for chemical precipitation.' },
  { name: 'Primary Sedimentation', category: 'primary', type: 'Clarifier', bod: 30, cod: 25, tss: 60, footprint: 'Large', energy: 'Low', capex: 'Medium', opex: 'Low', desc: 'Gravity settling of settleable solids.' },
  { name: 'Lamella Clarifier', category: 'primary', type: 'Clarifier', bod: 25, cod: 20, tss: 70, footprint: 'Small', energy: 'Low', capex: 'Medium', opex: 'Low', desc: 'Inclined plate settling for compact footprint.' },
  { name: 'Halophilic MBBR', category: 'secondary', type: 'Biological', bod: 75, cod: 60, tss: 40, footprint: 'Medium', energy: 'Medium', capex: 'Medium', opex: 'Medium', desc: 'Moving bed biofilm for saline wastewater treatment.' },
  { name: 'ABR', category: 'secondary', type: 'Biological', bod: 80, cod: 65, tss: 50, footprint: 'Medium', energy: 'Low', capex: 'Low', opex: 'Low', desc: 'Anaerobic baffled reactor for high-strength wastewater.' },
  { name: 'Activated Sludge', category: 'secondary', type: 'Biological', bod: 95, cod: 85, tss: 90, footprint: 'Large', energy: 'High', capex: 'Medium', opex: 'Medium', desc: 'Conventional suspended growth aerobic treatment.' },
  { name: 'Extended Aeration', category: 'secondary', type: 'Biological', bod: 98, cod: 90, tss: 95, footprint: 'Large', energy: 'High', capex: 'Medium', opex: 'Medium', desc: 'Longer detention time for nitrification.' },
  { name: 'SBR', category: 'secondary', type: 'Biological', bod: 95, cod: 85, tss: 90, footprint: 'Medium', energy: 'Medium', capex: 'Medium', opex: 'Medium', desc: 'Sequencing batch reactor — compact and flexible.' },
  { name: 'MBR', category: 'secondary', type: 'Membrane', bod: 99, cod: 93, tss: 100, footprint: 'Small', energy: 'High', capex: 'Very High', opex: 'High', desc: 'Membrane bioreactor for highest effluent quality.' },
  { name: 'MBBR', category: 'secondary', type: 'Biological', bod: 90, cod: 80, tss: 50, footprint: 'Small', energy: 'Medium', capex: 'Medium', opex: 'Medium', desc: 'Moving bed biofilm reactor — attached growth.' },
  { name: 'Multimedia Filter', category: 'tertiary', type: 'Filtration', bod: 20, cod: 15, tss: 95, footprint: 'Small', energy: 'Low', capex: 'Medium', opex: 'Low', desc: 'Multi-layer granular media for suspended solids.' },
  { name: 'Cloth/Disk Filter', category: 'tertiary', type: 'Filtration', bod: 15, cod: 10, tss: 95, footprint: 'Small', energy: 'Low', capex: 'Medium', opex: 'Low', desc: 'Micronic cloth filtration for polishing.' },
  { name: 'GAC Contactor', category: 'tertiary', type: 'Adsorption', bod: 40, cod: 35, tss: 30, footprint: 'Medium', energy: 'Low', capex: 'High', opex: 'High', desc: 'Granular activated carbon for organics and odour.' },
  { name: 'RO System', category: 'tertiary', type: 'Membrane', bod: 0, cod: 95, tss: 100, footprint: 'Small', energy: 'Very High', capex: 'Very High', opex: 'Very High', desc: 'Reverse osmosis for dissolved solids removal.' },
  { name: 'UV Disinfection', category: 'disinfection', type: 'Disinfection', bod: 0, cod: 5, tss: 0, footprint: 'Very Small', energy: 'Medium', capex: 'Medium', opex: 'Medium', desc: 'UV-C irradiation for pathogen inactivation.' },
  { name: 'Chlorine Contact', category: 'disinfection', type: 'Disinfection', bod: 5, cod: 10, tss: 10, footprint: 'Medium', energy: 'Low', capex: 'Low', opex: 'Low', desc: 'Chlorine contact tank for final disinfection.' },
];

function RatingBar({ value, color }: { value: number; color: string }) {
  return (
    <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden w-16">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${value}%` }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="h-full rounded-full"
        style={{ backgroundColor: color }}
      />
    </div>
  );
}

const ratingColor = (val: number) => val >= 90 ? '#10b981' : val >= 70 ? '#06b6d4' : val >= 50 ? '#f59e0b' : '#ef4444';

export default function ProcessDatabasePage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');

  const filtered = equipment.filter(e => {
    const matchCat = activeCategory === 'all' || e.category === activeCategory;
    const matchSearch = e.name.toLowerCase().includes(search.toLowerCase()) || e.type.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Database}
        iconColor="text-cyan-400"
        title="Process Equipment Database"
        subtitle={`${equipment.length} equipment items — browse, filter, compare`}
        actions={
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Search equipment..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 rounded-lg text-sm text-white placeholder-slate-500 border border-slate-700 bg-slate-900 focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/10 outline-none transition-all"
            />
          </div>
        }
      />

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        {categories.map((cat) => {
          const Icon = cat.icon;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${
                activeCategory === cat.id
                  ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30'
                  : 'text-slate-400 border-slate-800/50 hover:text-slate-200 hover:bg-slate-800/50'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Equipment Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((eq, i) => (
          <motion.div
            key={eq.name}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(i * 0.02, 0.3) }}
            className="rb-card p-4 rb-card-hover"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-200">{eq.name}</h3>
                <span className="text-xs text-cyan-400">{eq.type}</span>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-700">{eq.footprint}</span>
            </div>

            <p className="text-xs text-slate-500 mb-3 leading-relaxed">{eq.desc}</p>

            {/* Reduction bars */}
            <div className="space-y-2 mb-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-500 w-8">BOD</span>
                <RatingBar value={eq.bod} color={ratingColor(eq.bod)} />
                <span className="text-[10px] text-slate-400 w-8 text-right">{eq.bod}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-500 w-8">COD</span>
                <RatingBar value={eq.cod} color={ratingColor(eq.cod)} />
                <span className="text-[10px] text-slate-400 w-8 text-right">{eq.cod}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-500 w-8">TSS</span>
                <RatingBar value={eq.tss} color={ratingColor(eq.tss)} />
                <span className="text-[10px] text-slate-400 w-8 text-right">{eq.tss}%</span>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-1.5 pt-3 border-t border-slate-800/50">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">Capex: {eq.capex}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">Opex: {eq.opex}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">Energy: {eq.energy}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="py-12 text-center text-sm text-slate-500">No equipment matches your search criteria.</div>
      )}
    </div>
  );
}
